import styled, { keyframes } from 'styled-components';
import { ColorPalette } from 'theme/colors';

import BaseButton from 'components/Buttons/Button';
import BaseCheckbox from 'components/Checkbox';
import BaseInput from 'components/Input';

interface ContainerProps {
  center?: boolean;
}

interface HeaderProps {
  black?: boolean;
}

interface ButtonsContainerProps {
  left?: boolean;
  center?: boolean;
}

interface NextButtonProps {
  noMargin?: boolean;
  hide?: boolean;
  background?: string;
  color?: string;
  alignLeft?: boolean;
}

interface CenteredImageProps {
  width: number;
  marginTop?: number | string;
  marginBottom?: number | string;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  text-align: ${({ center }) => (center ? 'center' : 'left')};
  margin: auto;
  max-width: 600px;

  @media ${({ theme }) => theme.device.mobile} {
    height: 100%;
  }
`;

export const Header = styled.h1<HeaderProps>`
  color: ${ColorPalette.HEX.CharlestonGreen};
  font-size: 30px;
  font-weight: 600;
  line-height: 32px;

  margin: 0 0 32px 0;

  @media ${({ theme }) => theme.device.mobile} {
    color: ${ColorPalette.HEX.CharlestonGreen};
    font-size: 20px;
    line-height: 28px;

    margin: 0 0 16px 0;
  }
`;

export const Subheader = styled.h2`
  color: ${ColorPalette.HEX.DimGray};
  font-size: 14px;
  font-weight: normal;
  line-height: 24px;

  margin: 0 0 16px 0;

  & > strong {
    font-weight: 600;
  }

  & > em {
    color: ${ColorPalette.HEX.CharlestonGreen};
  }

  @media ${({ theme }) => theme.device.mobile} {
    font-size: 14px;
    line-height: 24px;

    margin: 0 0 8px 0;
  }
`;

export const Detail = styled.h3`
  color: ${ColorPalette.HEX.Manatee};
  font-size: 16px;
  font-weight: normal;
  line-height: 24px;

  margin: 0 0 30px 0;
`;

export const FieldsContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-gap: 12px;
`;

export const Input = styled(BaseInput)`
  @media ${({ theme }) => theme.device.desktop} {
    * > input {
      font-size: 18px;
      height: 60px;
    }
  }
`;

export const PhoneInput = styled(BaseInput)`
  overflow: visible;

  * > input {
    border-radius: 8px;
  }

  & > div {
    display: flex;
    flex-direction: row-reverse;
  }

  @media ${({ theme }) => theme.device.desktop} {
    * > input {
      font-size: 18px;
      height: 60px;
    }
  }
`;

export const CountryCodeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
`;

export const Checkbox = styled(BaseCheckbox)`
  display: flex;
  align-items: flex-start;
`;

export const TermsContainer = styled.div`
  margin-top: 32px;
  color: ${ColorPalette.HEX.Manatee};
`;

export const TermsText = styled.span`
  font-size: 16px;
  line-height: 24px;
  margin-left: 12px;
  color: ${ColorPalette.HEX.DimGray};

  @media ${({ theme }) => theme.device.mobile} {
    color: ${ColorPalette.HEX.Manatee};
  }

  & > a {
    color: ${({ theme }) => theme.primaryBackgroundColor};
    text-decoration: none;
    cursor: pointer;

    @media ${({ theme }) => theme.device.mobile} {
      color: ${ColorPalette.HEX.CharlestonGreen};
    }
  }
`;

export const ButtonsContainer = styled.div<ButtonsContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-self: ${({ left, center }) => {
    if (left) return 'flex-start';
    if (center) return 'center';
    return 'flex-end';
  }};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-top: 32px;

  @media ${({ theme }) => theme.device.mobile} {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    background: ${ColorPalette.HEX.White};
    padding: 16px 16px 24px 16px;
    margin-top: 32px;
  }

  @media ${({ theme }) => theme.device.desktop} {
    & > button {
      ${({ center }) => (center ? 'margin: 0 auto' : '')};
    }
  }
`;

export const ButtonLine = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const NextButton = styled(BaseButton)<NextButtonProps>`
  width: 280px;
  font-size: 16px;
  font-weight: 600;
  height: 50px;
  margin-left: ${({ noMargin }) => (noMargin ? 0 : '56px')};
  margin-top: ${({ noMargin }) => (noMargin ? 0 : '16px')};
  display: ${({ hide }) => (hide ? 'none' : '')};
  background: ${({ background }) => (background ? background : '')};
  text-align: ${({ alignLeft }) => (alignLeft ? 'left' : '')};
  border: 1px solid #e7e0e0;

  @media ${({ theme }) => theme.device.mobile} {
    min-width: none;
    height: 56px;
    font-size: 16px;
    width: 100%;
    margin-left: 0;
  }
`;

export const ResendButton = styled(BaseButton)`
  width: fit-content;
  text-align: right;
  font-weight: normal;
  font-size: 16px;
  height: 40px;

  background: none;
  color: ${({ theme }) => theme.primaryBackgroundColor};

  align-self: flex-end;

  @media ${({ theme }) => theme.device.mobile} {
    height: 56px;
    font-size: 16px;
    width: 100%;
    margin-left: 0;
    text-align: center;
  }
`;

export const BackButton = styled.button`
  height: 60px;

  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryBackgroundColor};
  background: none;
  cursor: pointer;

  margin-top: 16px;

  @media ${({ theme }) => theme.device.mobile} {
    display: none;
  }
`;

export const CenteredImage = styled.img<CenteredImageProps>`
  width: ${({ width }) => width}px;
  margin-top: ${({ marginTop }) => {
    if (typeof marginTop === 'number') return `${marginTop}px`;
    return marginTop || 0;
  }};
  margin-bottom: ${({ marginBottom }) => {
    if (typeof marginBottom === 'number') return `${marginBottom}px`;
    return marginBottom || 0;
  }};
  align-self: center;
`;

export const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #f4bf31;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite; /* Use a animação spin */
`;
